<script>
// NOTICE: KAPTION-INTEGRATION - Updated to support Follow-up and Schedule modes for WhatsApp/Twilio
import { computed } from 'vue';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { REPLY_EDITOR_MODES, CHAR_LENGTH_WARNING } from './constants';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EditorModeToggle from './EditorModeToggle.vue';
// NOTICE: KAPTION-INTEGRATION - Chat preferences gear (KAPTION_VIEW_SWITCHER)
import KaptionChatPreferencesButton from 'dashboard/components/kaption/KaptionChatPreferencesButton.vue';
// NOTICE: KAPTION-INTEGRATION - "Plantilla" button (KAPTION_REPLYBOX_TEMPLATE_PICKER)
import KaptionReplyTemplateButton from 'dashboard/components/widgets/conversation/KaptionReplyTemplate/KaptionReplyTemplateButton.vue';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';

export default {
  name: 'ReplyTopPanel',
  components: {
    NextButton,
    EditorModeToggle,
    // NOTICE: KAPTION-INTEGRATION
    KaptionChatPreferencesButton,
    KaptionReplyTemplateButton,
  },
  props: {
    mode: {
      type: String,
      default: REPLY_EDITOR_MODES.REPLY,
    },
    isReplyRestricted: {
      type: Boolean,
      default: false,
    },
    isMessageLengthReachingThreshold: {
      type: Boolean,
      default: () => false,
    },
    charactersRemaining: {
      type: Number,
      default: () => 0,
    },
    // NOTICE: KAPTION-INTEGRATION - New props for Follow-up mode
    isWhatsAppChannel: {
      type: Boolean,
      default: false,
    },
    canReply: {
      type: Boolean,
      default: true,
    },
    // NOTICE: KAPTION-INTEGRATION - Whether follow-up mode is available (requires meta payment confirmed)
    isFollowupAvailable: {
      type: Boolean,
      default: false,
    },
    // NOTICE: KAPTION-INTEGRATION - Whether schedule tab should be shown (WhatsApp/Twilio channels)
    showScheduleTab: {
      type: Boolean,
      default: false,
    },
    showPrivateNote: {
      type: Boolean,
      default: true,
    },
    // NOTICE: KAPTION-INTEGRATION - Whether the user is editing a message
    isEditingMessage: {
      type: Boolean,
      default: false,
    },
    // NOTICE: KAPTION-INTEGRATION - reply-template-picker.BUTTON.1 / BUTTON.3
    // ReplyBox decides visibility (flag + channel + mode); teal while loaded.
    showTemplateButton: {
      type: Boolean,
      default: false,
    },
    isTemplateLoaded: {
      type: Boolean,
      default: false,
    },
  },
  // NOTICE: KAPTION-INTEGRATION - openTemplatePicker
  emits: ['setReplyMode', 'togglePopout', 'cancelEdit', 'openTemplatePicker'],
  setup(props, { emit }) {
    const setReplyMode = mode => {
      emit('setReplyMode', mode);
    };

    // NOTICE: KAPTION-INTEGRATION - Updated to handle Follow-up mode
    const handleReplyClick = () => {
      if (props.isReplyRestricted) return;
      // When follow-up is available, use FOLLOWUP mode
      const targetMode = props.isFollowupAvailable
        ? REPLY_EDITOR_MODES.FOLLOWUP
        : REPLY_EDITOR_MODES.REPLY;
      setReplyMode(targetMode);
    };

    const handleNoteClick = () => {
      if (!props.showPrivateNote) return;
      setReplyMode(REPLY_EDITOR_MODES.NOTE);
    };

    // NOTICE: KAPTION-INTEGRATION - Handler for Schedule tab
    const handleScheduleClick = () => {
      setReplyMode(REPLY_EDITOR_MODES.SCHEDULE);
    };

    // NOTICE: KAPTION-INTEGRATION - Handle direct mode setting from EditorModeToggle
    const handleSetMode = mode => {
      setReplyMode(mode);
    };

    // NOTICE: KAPTION-INTEGRATION - Updated toggle to handle Follow-up and Schedule modes
    // Tab order: Reply/Follow-up → Schedule → Private Note
    const handleModeToggle = () => {
      // For WhatsApp/Twilio with schedule tab, cycle through modes
      if (props.showScheduleTab) {
        if (props.mode === REPLY_EDITOR_MODES.NOTE) {
          // From NOTE -> REPLY or FOLLOWUP (cycle back to start)
          setReplyMode(
            props.isFollowupAvailable
              ? REPLY_EDITOR_MODES.FOLLOWUP
              : REPLY_EDITOR_MODES.REPLY
          );
        } else if (props.mode === REPLY_EDITOR_MODES.SCHEDULE) {
          // From SCHEDULE -> NOTE
          if (props.showPrivateNote) {
            setReplyMode(REPLY_EDITOR_MODES.NOTE);
          } else {
            setReplyMode(
              props.isFollowupAvailable
                ? REPLY_EDITOR_MODES.FOLLOWUP
                : REPLY_EDITOR_MODES.REPLY
            );
          }
        } else {
          // From REPLY/FOLLOWUP -> SCHEDULE
          setReplyMode(REPLY_EDITOR_MODES.SCHEDULE);
        }
      } else if (props.mode === REPLY_EDITOR_MODES.NOTE) {
        // Original 2-tab toggle behavior: From NOTE -> REPLY or FOLLOWUP
        setReplyMode(
          props.isFollowupAvailable
            ? REPLY_EDITOR_MODES.FOLLOWUP
            : REPLY_EDITOR_MODES.REPLY
        );
      } else if (props.showPrivateNote) {
        // Original 2-tab toggle behavior: From REPLY/FOLLOWUP -> NOTE
        setReplyMode(REPLY_EDITOR_MODES.NOTE);
      }
    };

    const keyboardEvents = {
      'Alt+KeyP': {
        action: () => handleNoteClick(),
        allowOnFocusedInput: true,
      },
      'Alt+KeyL': {
        action: () => handleReplyClick(),
        allowOnFocusedInput: true,
      },
      // NOTICE: KAPTION-INTEGRATION - Add keyboard shortcut for Schedule tab
      'Alt+KeyS': {
        action: () => {
          if (props.showScheduleTab) {
            handleScheduleClick();
          }
        },
        allowOnFocusedInput: true,
      },
    };
    useKeyboardEvents(keyboardEvents);

    // NOTICE: KAPTION-INTEGRATION - Chat preferences next to the maximize button
    const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
    const showChatPreferences = computed(() =>
      isKaptionFlagEnabled('KAPTION_VIEW_SWITCHER')
    );

    return {
      showChatPreferences,
      handleModeToggle,
      handleReplyClick,
      handleNoteClick,
      handleScheduleClick,
      handleSetMode,
      REPLY_EDITOR_MODES,
    };
  },
  computed: {
    replyButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.REPLY,
      };
    },
    noteButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.NOTE,
      };
    },
    charLengthClass() {
      return this.charactersRemaining < 0 ? 'text-n-ruby-9' : 'text-n-slate-11';
    },
    characterLengthWarning() {
      return this.charactersRemaining < 0
        ? `${-this.charactersRemaining} ${CHAR_LENGTH_WARNING.NEGATIVE}`
        : `${this.charactersRemaining} ${CHAR_LENGTH_WARNING.UNDER_50}`;
    },
  },
};
</script>

<template>
  <div class="flex justify-between h-[3.25rem] gap-2 ltr:pl-3 rtl:pr-3">
    <!-- NOTICE: KAPTION-INTEGRATION - Edit mode banner replaces mode toggle -->
    <div v-if="isEditingMessage" class="flex items-center gap-2 mt-3">
      <span class="i-lucide-pencil size-4 text-n-slate-11" />
      <span class="text-sm font-medium text-n-slate-12">
        {{ $t('CONVERSATION.EDIT_MESSAGE.EDITING') }}
      </span>
      <NextButton
        ghost
        xs
        slate
        :label="$t('CONVERSATION.EDIT_MESSAGE.CANCEL')"
        @click="$emit('cancelEdit')"
      />
    </div>
    <!-- NOTICE: KAPTION-INTEGRATION - Pass WhatsApp channel, canReply, isFollowupAvailable, and showScheduleTab props -->
    <EditorModeToggle
      v-else
      :mode="mode"
      :disabled="isReplyRestricted"
      :is-whats-app-channel="isWhatsAppChannel"
      :can-reply="canReply"
      :is-followup-available="isFollowupAvailable"
      :show-schedule-tab="showScheduleTab"
      :show-private-note="showPrivateNote"
      class="mt-3"
      @toggle-mode="handleModeToggle"
      @set-mode="handleSetMode"
    />
    <div class="flex items-center mx-4 my-0">
      <div v-if="isMessageLengthReachingThreshold" class="text-xs">
        <span :class="charLengthClass">
          {{ characterLengthWarning }}
        </span>
      </div>
    </div>
    <!-- NOTICE: KAPTION-INTEGRATION - Chat preferences gear sits right next to
         maximize; both share one box so justify-between can't pull them apart. -->
    <div class="flex items-start">
      <!-- NOTICE: KAPTION-INTEGRATION - Plantilla · gear · maximize -->
      <KaptionReplyTemplateButton
        v-if="showTemplateButton && !isEditingMessage"
        :active="isTemplateLoaded"
        @click="$emit('openTemplatePicker')"
      />
      <KaptionChatPreferencesButton
        v-if="showChatPreferences"
        direction="up"
        class="mt-3"
      />
      <NextButton
        ghost
        class="ltr:rounded-bl-md rtl:rounded-br-md ltr:rounded-br-none rtl:rounded-bl-none ltr:rounded-tl-none rtl:rounded-tr-none text-n-slate-11 ltr:rounded-tr-[11px] rtl:rounded-tl-[11px]"
        icon="i-lucide-maximize-2"
        @click="$emit('togglePopout')"
      />
    </div>
  </div>
</template>
